import { useQuery } from '@tanstack/react-query';
import { Skeleton } from '@volvo/vcdk-react';
import React from 'react';

import type { IndicatorType } from '@/service/vpc/types/IAftermarketCampaign';
import type { IUnit } from '@/service/vpc/types/IUnit';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import Dot from '@/components/Dot';
import { getCombinedCampaignsQueryOptions } from '@/service/vpc/queryOptions';
import { RETRYABLE_ERROR_STATUSES } from '@/service/vpc/types/IInstallableCampaign';
import { chassis, common, indicator } from '@/utils';

type Props = {
  unit: IUnit;
  showInstallation?: boolean;
};

const CampaignStatus: React.FC<Props> = ({ unit, showInstallation = true }) => {
  const unitsChassisIds = chassis.getUnitsChassisIds(unit);

  const { data: combinedCampaigns, isLoading: isLoadingCampaigns } = useQuery(
    getCombinedCampaignsQueryOptions({
      endCustomerUnitIds: [String(unit.endCustomerUnitId)],
      serialNumbers: chassis.getUnitSerialNumbers(unit),
    })
  );

  const getCampaignsChassisSeries = (chassisSeries: string): IndicatorType[] => {
    const campaigns = combinedCampaigns?.filter((campaign) => {
      const chassiid = chassis.getId({
        chassisNumber: campaign.data.chassisNumber,
        chassisSeries: campaign.data.chassisSeries,
      });
      return chassiid === chassisSeries;
    });

    const hasRetryableError = campaigns?.some((c) =>
      RETRYABLE_ERROR_STATUSES.has(c.data.installableCampaign?.status ?? ('' as never))
    );

    return (
      campaigns
        ?.flatMap((c) => c.data.aftermarketCampaignType.split('_') as IndicatorType[])
        .filter((type) => (showInstallation && hasRetryableError) || type !== 'Installation') || []
    );
  };

  if (isLoadingCampaigns) {
    return (
      <div className="flex gap-4">
        <Skeleton variant="ellipse" height={16} width={16} />
        <Skeleton variant="ellipse" height={16} width={16} />
        <Skeleton variant="ellipse" height={16} width={16} />
      </div>
    );
  }

  const campaigns = unitsChassisIds.flatMap((sn) => getCampaignsChassisSeries(sn));
  const uniqueCampaigns = Array.from(new Set(campaigns));
  if (uniqueCampaigns.length === 0) {
    return null;
  }

  return (
    <div className="mt-2 flex gap-4 md:mt-0">
      {uniqueCampaigns.map((campaign) => (
        <Dot key={common.slugify(campaign)} variant={indicator.getVariant(campaign)} />
      ))}
    </div>
  );
};

export default withComponentErrorBoundary(CampaignStatus);
